// ─────────────────────────────────────────────────────────────────────────────
// TagChip / GroupChip — soft tinted pills for tags + groups in lists
// ─────────────────────────────────────────────────────────────────────────────
//
// Used in the rule library, users page, etc. Two flavors:
//   <TagChip>   — for rule tags    (uniform brand-teal pattern)
//   <GroupChip> — for group names  (neutral surface)
//
// All TagChips render in the same brand-teal palette so they read as a single
// consistent pattern across a row. Earlier versions hashed each label to a
// random palette which made dense rows look like confetti.
// ─────────────────────────────────────────────────────────────────────────────

import { Box, Stack, Tooltip } from "@mui/material";

// ── TagChip ─────────────────────────────────────────────────────────────────

const TAG_PALETTE = {
	bg: "rgba(2, 213, 171, 0.12)",
	fg: "#066848",
	// Border bumped from 0.30 → 0.55 alpha so the chip outline crosses
	// WCAG 2.1 AA 1.4.11 non-text contrast (≥3:1 against white).
	border: "rgba(2, 213, 171, 0.55)"
} as const;

interface TagChipProps {
	label: string;
	onRemove?: () => void;
	size?: "sm" | "md";
	/** Override the default 240px chip cap. Permissions like "Manage Organization Killswitch" need ~210px to fit; rare names go wider. */
	maxWidth?: number | string;
}

export function TagChip({ label, onRemove, size = "sm", maxWidth = 240 }: TagChipProps) {
	const isSm = size === "sm";
	return (
		<Box
			component="span"
			sx={{
				display: "inline-flex",
				alignItems: "center",
				gap: 0.5,
				px: isSm ? 1 : 1.25,
				py: isSm ? 0.25 : 0.375,
				borderRadius: 999,
				background: TAG_PALETTE.bg,
				color: TAG_PALETTE.fg,
				border: `1px solid ${TAG_PALETTE.border}`,
				fontSize: isSm ? 11 : 12,
				fontWeight: 600,
				letterSpacing: "0.02em",
				lineHeight: 1.4,
				whiteSpace: "nowrap",
				maxWidth,
				overflow: "hidden",
				textOverflow: "ellipsis"
			}}
		>
			{label}
			{onRemove && (
				<Box
					component="button"
					onClick={(e) => {
						e.stopPropagation();
						onRemove();
					}}
					aria-label={`Remove ${label}`}
					sx={{
						all: "unset",
						display: "inline-flex",
						alignItems: "center",
						justifyContent: "center",
						width: 14,
						height: 14,
						borderRadius: "50%",
						cursor: "pointer",
						opacity: 0.55,
						transition: "opacity 0.18s",
						"&:hover": { opacity: 1 }
					}}
				>
					<Box component="span" sx={{ fontSize: 14, fontWeight: 700, lineHeight: 1 }}>×</Box>
				</Box>
			)}
		</Box>
	);
}

// ── GroupChip ───────────────────────────────────────────────────────────────

interface GroupChipProps {
	label: string;
	onRemove?: () => void;
	size?: "sm" | "md";
	maxWidth?: number | string;
}

export function GroupChip({ label, onRemove, size = "sm", maxWidth = 240 }: GroupChipProps) {
	const isSm = size === "sm";
	return (
		<Box
			component="span"
			sx={{
				display: "inline-flex",
				alignItems: "center",
				gap: 0.5,
				px: isSm ? 1 : 1.25,
				py: isSm ? 0.25 : 0.375,
				borderRadius: 999,
				background: "rgba(13, 31, 48, 0.06)",
				color: "rgba(13, 31, 48, 0.78)",
				border: "1px solid rgba(13, 31, 48, 0.10)",
				fontSize: isSm ? 11 : 12,
				fontWeight: 600,
				letterSpacing: "0.02em",
				lineHeight: 1.4,
				whiteSpace: "nowrap",
				maxWidth,
				overflow: "hidden",
				textOverflow: "ellipsis"
			}}
		>
			{label}
			{onRemove && (
				<Box
					component="button"
					onClick={(e) => {
						e.stopPropagation();
						onRemove();
					}}
					aria-label={`Remove ${label}`}
					sx={{
						all: "unset",
						display: "inline-flex",
						alignItems: "center",
						justifyContent: "center",
						width: 14,
						height: 14,
						borderRadius: "50%",
						cursor: "pointer",
						opacity: 0.55,
						"&:hover": { opacity: 1 }
					}}
				>
					<Box component="span" sx={{ fontSize: 14, fontWeight: 700, lineHeight: 1 }}>×</Box>
				</Box>
			)}
		</Box>
	);
}

// ── ChipList — wraps multiple chips with consistent gap + flex wrap ─────────

interface ChipListProps {
	children: React.ReactNode;
	emptyLabel?: string;
}

// ── CondensedChipList ───────────────────────────────────────────────────────
// Shows up to `max` chips on a single line, then condenses the remainder into a
// "+N" pill. Hovering the "+N" pill reveals the hidden labels in a tooltip.
// Keeps dense cells (permissions, groups, etc.) from overflowing / clipping.

interface CondensedChipListProps {
	labels: string[];
	/** How many chips to show before condensing the rest into "+N". */
	max?: number;
	emptyLabel?: string;
	chipMaxWidth?: number | string;
}

export function CondensedChipList({ labels, max = 3, emptyLabel, chipMaxWidth }: CondensedChipListProps) {
	const safe = (Array.isArray(labels) ? labels : []).filter(Boolean);
	if (safe.length === 0) {
		return emptyLabel ? (
			<Box component="span" sx={{ color: "rgba(13, 31, 48, 0.40)", fontStyle: "italic", fontSize: 12 }}>
				{emptyLabel}
			</Box>
		) : null;
	}
	const visible = safe.slice(0, max);
	const hidden = safe.slice(max);
	return (
		<Stack direction="row" spacing={0.5} useFlexGap sx={{ alignItems: "center", flexWrap: "nowrap", overflow: "hidden", width: "100%", minWidth: 0 }}>
			{visible.map((label, i) => (
				<TagChip key={`${label}-${i}`} label={label} maxWidth={chipMaxWidth} />
			))}
			{hidden.length > 0 && (
				<Tooltip
					arrow
					placement="top"
					title={
						<Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, py: 0.25, maxWidth: 320 }}>
							{hidden.map((label, i) => (
								<Box component="span" key={`${label}-${i}`} sx={{ fontSize: 12, fontWeight: 500 }}>{label}</Box>
							))}
						</Box>
					}
				>
					<Box
						component="span"
						sx={{
							display: "inline-flex",
							alignItems: "center",
							flexShrink: 0,
							px: 1,
							py: 0.25,
							borderRadius: 999,
							background: TAG_PALETTE.bg,
							color: TAG_PALETTE.fg,
							border: `1px solid ${TAG_PALETTE.border}`,
							fontSize: 11,
							fontWeight: 700,
							lineHeight: 1.4,
							whiteSpace: "nowrap",
							cursor: "default",
						}}
					>
						+{hidden.length}
					</Box>
				</Tooltip>
			)}
		</Stack>
	);
}

export function ChipList({ children, emptyLabel }: ChipListProps) {
	const items = Array.isArray(children) ? children : [children];
	const hasContent = items.filter(Boolean).length > 0;
	if (!hasContent && emptyLabel) {
		return (
			<Box component="span" sx={{ color: "rgba(13, 31, 48, 0.40)", fontStyle: "italic", fontSize: 12 }}>
				{emptyLabel}
			</Box>
		);
	}
	return (
		<Stack direction="row" spacing={0.5} sx={{ flexWrap: "wrap" }}>
			{children}
		</Stack>
	);
}

export default TagChip;
